/*! Copyright (c) 2024, XAPP AI */
import { FormWidgetTheme, FormWidgetAppChannelInput } from "generated/graphql";

import { cloneDeep } from "lodash";

import { getApiUrl } from "./getApiUrl";
import { darkenColor, invertColor } from "./color";

const BASE_THEME: FormWidgetTheme = {
    accentColor: "#012b54",

    backgroundColor: "#f3f8fb",

    headerBackgroundColor: "#012b54",
    headerTextColor: "#b7c5d1",

    primaryButtonColor: "#ea6539",
    primaryButtonTextColor: "#b7c5d1",
    secondaryButtonColor: "#d567cc",
    secondaryButtonTextColor: "#b7c5d1",

    standAlone: {
        backgroundColor: "#b8b8b8",
        header: {
            backgroundColor: "#ffffff",
            color: "#242424",
        },
    },

    text: {
        backgroundColor: "rgba(0, 0, 0, 0.06)",
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: "1rem",
    },
    dropdown: {
        backgroundColor: "#ffffff",
        color: "#000000",
        fontSize: "1rem",
    },
    checkbox: {
        backgroundColor: "#ffffff",
        color: "#000000",
        fontSize: "1rem",
    },
    chips: {
        backgroundColor: "#c4dced",
        backgroundColorSelected: "#6e7ae1",
        color: "#ffffff",
        colorSelected: "#000000",
        fontSize: "0.875em",
    },
    date: {
        backgroundColor: "#ffffff",
        backgroundColorSelected: "#a21a1a",
        color: "#000000",
        fontSize: "1rem",
    },
    card: {
        backgroundColor: "#ffffff",
        color: "#000000",
        fontSize: "1rem",
    },
    sideButton: {
        backgroundColor: "#d8412a",
        color: "#ffffff",
        fontSize: "1.5rem",
        minLength: "200px",
        position: "LEFT",
        top: "50%",
    },
};

/**
 * Generates a default theme for the form widget
 *
 * @param accentColor
 * @returns
 */
export function buildDefaultFormTheme(accentColor?: string): FormWidgetTheme {
    if (!accentColor) {
        return BASE_THEME;
    }

    return generateDefaultTheme(BASE_THEME, accentColor);
}

/**
 * Generates a theme based on a primary color
 *
 * NOTE: Use buildDefaultFormTheme instead
 *
 * @param BASE_THEME
 * @param primaryColor
 * @returns
 */
export function generateDefaultTheme(BASE_THEME: FormWidgetTheme, primaryColor: string): FormWidgetTheme {
    const theme = cloneDeep(BASE_THEME);

    const textColor = invertColor(primaryColor, true);

    theme.accentColor = primaryColor;
    theme.headerBackgroundColor = primaryColor;
    theme.headerTextColor = textColor;

    theme.sideButton = {
        backgroundColor: primaryColor,
        color: textColor,
    };

    theme.chips = {
        backgroundColor: primaryColor,
        color: textColor,
        colorSelected: textColor,
    };

    // booking page
    theme.standAlone = {
        backgroundColor: "#b8b8b8",
        header: {
            backgroundColor: "#ffffff",
            color: "#242424",
        },
    };

    const darkerAccentColor = darkenColor(primaryColor, 20);
    //   const darkAccentColor = darkenColor(primaryColor, 30);

    // const lighterAccentColor = darkenColor(accentColor, -20);
    // const lightAccentColor = darkenColor(accentColor, -30);

    theme.secondaryButtonColor = darkerAccentColor;

    // theme.headerBackgroundColor = darkAccentColor;
    // theme.headerTextColor = lightAccentColor;

    // theme.backgroundColor = lightAccentColor;

    // theme.text.color = darkAccentColor;

    // theme.chips.backgroundColor = lighterAccentColor;
    // theme.chips.colorSelected = darkerAccentColor;
    // theme.chips.color = darkAccentColor;
    // theme.chips.colorSelected = lightAccentColor;

    return theme;
}

export interface FormWidgetInput {
    name: string;
    website?: string;
    primaryColor: string;
    secondaryColor?: string;
    appId: string;
    sideButtonLabel?: string;
    /**
     * Used to add the chat widget to the booking page
     */
    chatWidgetKey?: string;
    /**
     *
     */
    logo?: string;
    /**
     * Displayed to the user
     */
    address?: {
        streetAddress?: string;
    };
}

export function generateFormWidgetInput(appInput: FormWidgetInput): FormWidgetAppChannelInput {
    const { appId, address, primaryColor, name, logo, chatWidgetKey, website } = appInput;

    const apiUrl = getApiUrl();

    const sideButtonLabel: string = appInput.sideButtonLabel || "Contact Us";

    const input: FormWidgetAppChannelInput = {
        type: "form-widget",
        name: "Booking Widget",
        connection: {
            serverUrl: `${apiUrl}/apps/${appId}/run`,
        },
        autoGreeting: "contact us",
        intentId: "LeadGeneration",
        sideButtonLabel,

        businessName: name,
        businessLogoUrl: logo,
        businessAddress: address?.streetAddress,
        businessWebsite: website,
        chatWidgetKey: chatWidgetKey,

        theme: generateDefaultTheme(BASE_THEME, primaryColor),
    };

    return input;
}
